/* ================================================================
   Operations layer - modifyTool equivalent.
   All mutations produce a new ProjectModel (immutable transforms).
   Uses structuredClone for fast, reliable deep copies.
   ================================================================ */

import type {
  ProjectModel, Side, SideId, LayerStyle, Dimensions3,
  StyleSetting, BlockBoardConfig, Vec3, ElementOverride,
} from '../model/types';
import { SIDE_ID_LIST } from '../model/types';
import { createDefaultLayer, defaultBlockBoard, defaultThickness } from '../model/defaults';

// --------------- Helpers ---------------

const hasStructuredClone = typeof (globalThis as any).structuredClone === 'function';

function cloneProject(p: ProjectModel): ProjectModel {
  if (hasStructuredClone) return (globalThis as any).structuredClone(p);
  return JSON.parse(JSON.stringify(p));
}

function findSide(project: ProjectModel, sideId: SideId): Side | undefined {
  return project.sides.find(s => s.sideId === sideId);
}

function sideIndex(sideId: SideId): number {
  return SIDE_ID_LIST.indexOf(sideId);
}

function touch(p: ProjectModel): void {
  p.meta.updatedAt = new Date().toISOString();
}

/**
 * 根据各面层厚度自动计算外尺寸。
 * 外高 = 底座总厚 + 顶盖总厚 + 内高
 * 外长 = 左端总厚 + 右端总厚 + 内长
 * 外宽 = 前面总厚 + 后面总厚 + 内宽
 */
function recalcOuterSize(p: ProjectModel): void {
  const totals: Record<SideId, number> = {
    pedestal: 0, topCap: 0, sideFront: 0, sideBack: 0, endLeft: 0, endRight: 0,
  };
  for (const side of p.sides) {
    if (!side.enabled) continue;
    let t = 0;
    for (const l of side.layers) t += l.thickness;
    totals[side.sideId] = t;
  }
  p.outerSize = {
    length: p.innerSize.length + totals.endLeft + totals.endRight,
    width: p.innerSize.width + totals.sideFront + totals.sideBack,
    height: p.innerSize.height + totals.pedestal + totals.topCap,
  };

  // independent 模式下同步更新 sideSpanOverrides，避免加层后面板尺寸/位置对不上
  if (p.sideLinkageMode === 'independent' && p.sideSpanOverrides) {
    const sideHeight = Math.max(0, p.outerSize.height - totals.pedestal - totals.topCap);
    p.sideSpanOverrides = {
      pedestal: { spanU: p.outerSize.length, spanV: p.outerSize.width, offsetU: 0, offsetV: 0 },
      topCap: { spanU: p.outerSize.length, spanV: p.outerSize.width, offsetU: 0, offsetV: 0 },
      sideFront: { spanU: p.outerSize.length, spanV: sideHeight, offsetU: 0, offsetV: 0 },
      sideBack: { spanU: p.outerSize.length, spanV: sideHeight, offsetU: 0, offsetV: 0 },
      endLeft: { spanU: p.outerSize.width, spanV: sideHeight, offsetU: 0, offsetV: 0 },
      endRight: { spanU: p.outerSize.width, spanV: sideHeight, offsetU: 0, offsetV: 0 },
    };
  }
}

function reindex(side: Side): void {
  const si = sideIndex(side.sideId);
  for (let i = 0; i < side.layers.length; i++) {
    const l = side.layers[i];
    l.layerIndex = i;
    l.recordsNo = si * 10 + i;
    l.isBase = i === 0;
  }
}

// --------------- Size operations ---------------

export function changeInnerSize(
  project: ProjectModel,
  size: Partial<Dimensions3>,
): ProjectModel {
  const p = cloneProject(project);
  Object.assign(p.innerSize, size);
  recalcOuterSize(p);
  touch(p);
  return p;
}

export function changeOuterSize(
  project: ProjectModel,
  size: Partial<Dimensions3>,
): ProjectModel {
  const p = cloneProject(project);
  Object.assign(p.outerSize, size);
  touch(p);
  return p;
}

// --------------- Layer property operations ---------------

export interface LayerPatch {
  style?: LayerStyle;
  direction?: 0 | 1;
  thickness?: number;
  materialKey?: number;
  color?: string;
  offset?: Partial<Vec3>;
  mirrored?: boolean;
  name?: string;
  blockBoard?: Partial<BlockBoardConfig>;
  countBoard?: number;
  countFrame?: number[];
  countHorBrace?: number[];
}

function applyLayerPatch(layer: any, patch: LayerPatch, sideId?: SideId): boolean {
  let thicknessChanged = false;
  if (patch.style !== undefined) {
    layer.style = patch.style;
    layer.styleSetting.style = patch.style;
    layer.profile = styleLabel(patch.style);
    layer.thickness = defaultThickness(patch.style, sideId);
    thicknessChanged = true;

    if (patch.style === 0) {
      layer.blockBoard = defaultBlockBoard(patch.style);
      layer.styleSetting.count.board = layer.blockBoard.count;
      layer.styleSetting.count.frame = [];
    } else if (patch.style === 3) {
      layer.blockBoard = defaultBlockBoard(patch.style);
      layer.styleSetting.count.board = 1;
      layer.styleSetting.count.frame = [2, 2];
      layer.styleSetting.count.horBrace = [1];
    } else if (patch.style === 4) {
      layer.blockBoard = defaultBlockBoard(patch.style);
      layer.styleSetting.count.board = 0;
      layer.styleSetting.count.frame = [3, 3];
    } else {
      layer.blockBoard = defaultBlockBoard(patch.style);
      layer.styleSetting.count.board = patch.style === 1 ? 1 : 0;
    }
  }
  if (patch.direction !== undefined) {
    layer.direction = patch.direction;
    layer.styleSetting.directions.isHor = patch.direction;
    layer.styleSetting.directions.frame = patch.direction;
    layer.styleSetting.directions.horBrace = patch.direction === 0 ? 1 : 0;
  }
  if (patch.thickness !== undefined) {
    layer.thickness = patch.thickness;
    thicknessChanged = true;
  }
  if (patch.materialKey !== undefined) {
    layer.materialKey = patch.materialKey;
    layer.styleSetting.material = patch.materialKey;
  }
  if (patch.color !== undefined) {
    layer.color = patch.color;
    layer.styleSetting.materialcolor = patch.color;
  }
  if (patch.offset) Object.assign(layer.offset, patch.offset);
  if (patch.mirrored !== undefined) layer.mirrored = patch.mirrored;
  if (patch.name !== undefined) layer.name = patch.name;
  if (patch.blockBoard) Object.assign(layer.blockBoard, patch.blockBoard);
  if (patch.blockBoard?.count !== undefined && layer.style === 0) {
    layer.styleSetting.count.board = patch.blockBoard.count;
  }
  if (patch.countBoard !== undefined) {
    layer.styleSetting.count.board = patch.countBoard;
    if (layer.style === 0) {
      layer.blockBoard.count = patch.countBoard;
    }
  }
  if (patch.countFrame !== undefined) layer.styleSetting.count.frame = patch.countFrame.slice();
  if (patch.countHorBrace !== undefined) layer.styleSetting.count.horBrace = patch.countHorBrace.slice();
  // 层级参数变了，清空单根覆盖（回到统一模式）
  layer.elementOverrides = undefined;
  return thicknessChanged;
}

export function changeLayerProps(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
  patch: LayerPatch,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || layerIndex < 0 || layerIndex >= side.layers.length) return p;

  const thicknessChanged = applyLayerPatch(side.layers[layerIndex], patch, sideId);
  if (thicknessChanged) recalcOuterSize(p);
  touch(p);
  return p;
}

// --------------- Single element override ---------------

export interface ElementPatch {
  length?: number;
  width?: number;
  thickness?: number;
}

export function changeElementProps(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
  elementIndex: number,
  patch: ElementPatch,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || layerIndex < 0 || layerIndex >= side.layers.length) return p;

  const layer = side.layers[layerIndex];
  if (!layer.elementOverrides) layer.elementOverrides = [];

  const existing = layer.elementOverrides.find(o => o.index === elementIndex);
  if (existing) {
    if (patch.length !== undefined) existing.length = patch.length;
    if (patch.width !== undefined) existing.width = patch.width;
    if (patch.thickness !== undefined) existing.thickness = patch.thickness;
  } else {
    layer.elementOverrides.push({ index: elementIndex, ...patch });
  }

  touch(p);
  return p;
}

function styleLabel(s: LayerStyle): string {
  const map: Record<number, string> = {
    0: 'Board', 1: 'Solid Panel', 2: 'Frame', 3: 'Custom',
    4: 'Timber', 5: 'Slotted', 6: 'Slotted Simple',
  };
  return map[s] ?? 'Unknown';
}

// --------------- StyleSetting sub-operations ---------------

export function changeStyleLogicSize(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
  key: keyof StyleSetting['logics']['size'],
  value: number,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || !side.layers[layerIndex]) return p;
  side.layers[layerIndex].styleSetting.logics.size[key] = value;
  touch(p);
  return p;
}

export function changeStyleLogicSpace(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
  key: keyof StyleSetting['logics']['space'],
  value: number,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || !side.layers[layerIndex]) return p;
  side.layers[layerIndex].styleSetting.logics.space[key] = value;
  touch(p);
  return p;
}

export function changeStyleLogicPosition(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
  patch: Partial<StyleSetting['logics']['position']>,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || !side.layers[layerIndex]) return p;
  Object.assign(side.layers[layerIndex].styleSetting.logics.position, patch);
  touch(p);
  return p;
}

export function changeStyleCount(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
  patch: Partial<StyleSetting['count']>,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || !side.layers[layerIndex]) return p;
  const c = side.layers[layerIndex].styleSetting.count;
  if (patch.frame !== undefined) c.frame = patch.frame.slice();
  if (patch.board !== undefined) c.board = patch.board;
  if (patch.horBrace !== undefined) c.horBrace = patch.horBrace.slice();
  if (patch.bevelBrace !== undefined) c.bevelBrace = patch.bevelBrace.slice();
  touch(p);
  return p;
}

// --------------- Layer insert / remove ---------------

export function insertLayer(
  project: ProjectModel,
  sideId: SideId,
  insertIndex: number,
  style: LayerStyle = 0,
  direction: 0 | 1 = 0,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side) return p;

  const idx = Math.max(0, Math.min(insertIndex, side.layers.length));
  const newLayer = createDefaultLayer(sideId, idx, style, direction);
  side.layers.splice(idx, 0, newLayer);

  reindex(side);
  recalcOuterSize(p);
  touch(p);
  return p;
}

export function removeLayer(
  project: ProjectModel,
  sideId: SideId,
  layerIndex: number,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (!side || layerIndex < 0 || layerIndex >= side.layers.length) return p;
  if (side.layers.length <= 1) return p; // keep at least one layer

  side.layers.splice(layerIndex, 1);
  reindex(side);
  recalcOuterSize(p);
  touch(p);
  return p;
}

// --------------- Side enable/disable ---------------

export function toggleSide(
  project: ProjectModel,
  sideId: SideId,
  enabled: boolean,
): ProjectModel {
  const p = cloneProject(project);
  const side = findSide(p, sideId);
  if (side) side.enabled = enabled;
  recalcOuterSize(p);
  touch(p);
  return p;
}

// --------------- Batch operations ---------------

export function batchChangeLayerProps(
  project: ProjectModel,
  targets: Array<{ sideId: SideId; layerIndex: number }>,
  patch: LayerPatch,
): ProjectModel {
  const p = cloneProject(project);
  let thicknessChanged = false;
  for (const t of targets) {
    const side = findSide(p, t.sideId);
    if (!side || !side.layers[t.layerIndex]) continue;
    if (applyLayerPatch(side.layers[t.layerIndex], patch, t.sideId)) {
      thicknessChanged = true;
    }
  }
  if (thicknessChanged) recalcOuterSize(p);
  touch(p);
  return p;
}

export function removeMultipleLayers(
  project: ProjectModel,
  targets: Array<{ sideId: SideId; layerIndex: number }>,
): ProjectModel {
  const p = cloneProject(project);
  const grouped = new Map<SideId, number[]>();
  for (const t of targets) {
    const arr = grouped.get(t.sideId) ?? [];
    arr.push(t.layerIndex);
    grouped.set(t.sideId, arr);
  }

  for (const [sid, indices] of grouped) {
    const side = findSide(p, sid);
    if (!side) continue;
    const sorted = [...new Set(indices)].sort((a, b) => b - a);
    for (const idx of sorted) {
      if (idx >= 0 && idx < side.layers.length && side.layers.length > 1) {
        side.layers.splice(idx, 1);
      }
    }
    reindex(side);
  }

  recalcOuterSize(p);
  touch(p);
  return p;
}
